/**
 * Forgot Password Page (Web)
 * Password reset functionality
 * 
 * ⚠️ IMPORTANT: Per ADR-FE-001, this component only handles UI.
 * Password reset is handled by Firebase.
 */

import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Typography,
  Box,
  Container,
} from '@mui/material';
import { useAuth } from '@shared/features/auth';

export function ForgotPasswordPage() {
  const { resetPassword, loading, error } = useAuth();
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSuccess(false);
    try {
      await resetPassword(email);
      setSuccess(true);
    } catch (err) {
      // Error handled by useAuth
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 400, padding: 3 }}>
          <CardContent>
            <Typography variant="h4" component="h1" gutterBottom align="center">
              FlowRithm
            </Typography>
            <Typography variant="h6" component="h2" gutterBottom align="center" color="text.secondary">
              Reset Password
            </Typography>

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Password reset email sent! Please check your inbox.
              </Alert>
            )}

            {error && !success && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {!success ? (
              <form onSubmit={handleSubmit}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Enter your email address and we&apos;ll send you a link to reset your password.
                </Typography>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                  margin="normal"
                  required
                  autoComplete="email"
                  autoFocus
                />
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  sx={{ mt: 3, mb: 2 }}
                  size="large"
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>
            ) : (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check your email for password reset instructions.
                </Typography>
                <Button component={Link} to="/login" variant="outlined" fullWidth>
                  Back to Login
                </Button>
              </Box>
            )}

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2">
                Remember your password?{' '}
                <Link to="/login" style={{ textDecoration: 'none' }}>
                  Sign in
                </Link>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}

